/**
 * The Loading component, used while resources load
 * Keep this component as similar as possible to the resource page importing it
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  Button,
  ListGroupItem,
  ListGroup,
  CardImg,
  CardBody,
  CardTitle,
  CardText,
  Card,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Collapse,
} from "reactstrap";
import FormattedNumber from "../../FormattedNumber";
import fileguideIcon from "../../../assets/images/icons/fileguide_colored.svg";
import icon_crop from "../../../assets/images/icons/crop.svg";
import icon_filesize from "../../../assets/images/icons/filesize.svg";
import {
  upload as uploadFile,
  uploadSuccess,
  reset as resetFileUpload,
  startCrop,
  endCrop,
} from "../../../redux/cop/artworks";
import { FaCaretDown, FaCaretUp, FaRegClock, FaTrash } from "react-icons/fa";
import { REACT_APP_COP_SERVER } from "../../../utils/env";
import {
  FILEEDITOR,
  FILEEDITORTYPES,
} from "../../FileUploadEditorForm/utils/fileEditor";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import FileUploadPreview from "../../FileUploadEditorForm/utils/FileUploadPreview";
import EditorFormHelpers from "../../FileUploadEditorForm/utils/FileUploadEditorFormHelpers";
import { useDispatch, useSelector } from "react-redux";
import FileSizeModal from "../../FileUploadEditorForm/utils/FileSizeModal";
const FileTab = (
  { editorTypeId, mobileVisible, continueToNextStep },
  context
) => {
  const language = useSelector((state) => {
    return state.i18nState.lang;
  });
  const { control, setValue } = useFormContext();
  const [showFilecheckWarning, setShowFilecheckWarning] = useState(false);

  const [showFilecheckMoreInformation, setShowFilecheckMoreInformation] =
    useState(false);

  const artworkId = useWatch({
    control,
    name: "artworkId",
  });

  const { artwork, currentArtworkId } = useSelector((state) => ({
    artwork: state.artworks.byIdDictionary[artworkId],
    currentArtworkId: state.artworks.currentArtworkId,
  }));

  const fileuploadCheckbox = useWatch({
    control,
    name: "filecheck_checkbox",
  });
  const width = useWatch({
    control,
    name: "width",
  });
  const skipFileUpload = useWatch({
    control,
    name: "skip_file_upload",
  });
  const height = useWatch({
    control,
    name: "height",
  });
  const cutting = useWatch({
    control,
    name: "cutting",
  });
  const sku = useWatch({
    control,
    name: "sku",
  });

  const file = artwork?.data?.attributes;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!artwork || !artwork.data?.id) {
      console.log("noid");
      return;
    }

    // Check if all attributes are set before opening SSE
    if (
      artwork?.data?.attributes?.preview &&
      ((artwork?.data?.attributes?.width &&
        artwork?.data?.attributes?.height) ||
        artwork.data.attributes?.analyzed_at)
    ) {
      console.log("All data set:", artwork.data);
      return;
    }

    if (artwork.data.attributes?.surrender) {
      alert(
        "Something went wrong generating your Preview Image. Please reload the Page and order without direct upload."
      );
      return;
    }

    const evtSource = new EventSource(
      `${REACT_APP_COP_SERVER}/api/v1/artworks/status_stream?id=${artwork.data.id}`
    );

    evtSource.onmessage = (event) => {
      console.log("AW Event", event);

      if (event.data) {
        const updatedArtwork = JSON.parse(event.data);
        dispatch(uploadSuccess(updatedArtwork));

        // Check if all attributes are now set
        if (
          updatedArtwork.data.attributes.width &&
          updatedArtwork.data.attributes.height
        ) {
          // !sizeChanged
          if (
            updatedArtwork?.data?.attributes?.width &&
            updatedArtwork?.data?.attributes?.height &&
            false
          ) {
            const scaledWidth = Math.min(
              Math.round(updatedArtwork?.data?.attributes?.width / 3),
              1500
            );
            const scaledheight = Math.min(
              Math.round(updatedArtwork?.data?.attributes?.height / 3),
              1500
            );

            if (scaledWidth > 1500 || scaledheight > 1500) {
              if (scaledWidth > scaledheight) {
                setValue("width", 1500);
                setValue("height", 1500 * (scaledheight / scaledWidth));
              } else {
                setValue("width", 1500 * (scaledheight / scaledWidth));
                setValue("height", 1500);
              }
            } else {
              setValue("width", scaledWidth);
              setValue("height", scaledheight);
            }
            setValue("fileRatio", scaledheight / scaledWidth);
          }
        }
        if (
          updatedArtwork?.data?.attributes?.preview &&
          ((updatedArtwork?.data?.attributes?.width &&
            updatedArtwork?.data?.attributes?.height) ||
            updatedArtwork.data.attributes?.analyzed_at)
        ) {
          evtSource.close(); // Close the connection
        }
      }
    };

    evtSource.onerror = (error) => {
      console.error("EventSource error:", error);
      evtSource.close(); // Close on error
    };

    // Cleanup on unmount
    return () => {
      evtSource.close();
      console.log("EventSource closed on cleanup.");
    };
  }, [artwork, dispatch]);

  useEffect(() => {
    if (!sku && file) {
      dispatch(resetFileUpload());
      setValue("skip_file_upload", true);
    }
  }, [sku]);

  useEffect(() => {
    if (currentArtworkId) setValue("artworkId", currentArtworkId);
  }, [currentArtworkId]);

  useEffect(() => {
    if (!fileuploadCheckbox) setShowFilecheckWarning(true);
  }, [fileuploadCheckbox]);

  useEffect(() => {
    if (!artwork?.data?.attributes?.width) return;
    setValue("skip_file_upload", false);
  }, [artwork, setValue]);

  const allowedFormats = ["PDF", "AI", "SVG", "EPS", "PSD", "PNG", "JPG"];
  return (
    <Container>
      {!mobileVisible && (
        <p className="text-center h5">{context.t("Upload file now")}</p>
      )}

      <div
        className="fileeditor"
        style={{ minHeight: mobileVisible ? "50px" : "200px" }}
      >
        <div className="fileeditor__preview">
          <FileUploadPreview
            mobileVisible={mobileVisible}
            cropShape={
              FILEEDITOR[editorTypeId]
                ? FILEEDITOR[editorTypeId].cropShape
                : false
            }
            allowedFormats={allowedFormats}
            uploadFile={(args) => {
              console.log("DEBUG ARGS", args);
              dispatch(uploadFile(args));
            }}
            handleUpdateSku={() => {
              if (!sku)
                setValue("sku", EditorFormHelpers.generateSKU("designer"));
            }}
            file={artwork?.data?.attributes}
            minHeight={
              !skipFileUpload && file?.cropping
                ? false
                : mobileVisible
                ? "50px"
                : "200px"
            }
            forceWhiteBackground={
              cutting === "rectangular" &&
              editorTypeId !== FILEEDITORTYPES.STICKER_WHITEPRINT
            }
            formRatio={width / height}
            sku={sku}
            sizeChanged={false}
            acceptedMIMETypes={
              "image/gif, image/jpeg, image/pjpeg, image/png, application/octet-stream, application/postscript, application/pdf, image/svg+xml, image/tiff, image/tiff-fx, image/vnd.adobe.photoshop, application/x-photoshop, application/photoshop, application/vnd.adobe.photoshop, application/psd, image/psd, image/*, .psd"
            }
          />
        </div>
        <Controller
          name="artworkId"
          control={control}
          render={({ field }) => (
            <Input type="hidden" checked={field.value} {...field} />
          )}
        />
      </div>
      <div className="text-center mt-3">
        {!skipFileUpload && (
          <Button
            outline
            size="sm"
            color="danger"
            onClick={() => {
              setValue("artworkId", null);
              dispatch(resetFileUpload());
              setValue("skip_file_upload", true);
            }}
          >
            {context.t("discard uploaded file")} <FaTrash />
          </Button>
        )}
        {skipFileUpload && mobileVisible && (
          <div
            className="border rounded text-left p-2 pointer-primary mx-md-5"
            onClick={() => continueToNextStep()}
          >
            <Row>
              <Col xs={10}>
                <strong>{context.t("Upload file later")}</strong>
                <p className="text-secondary mb-0">
                  {context.t("Delivery your file after purchase")}
                </p>
              </Col>
              <Col
                xs={2}
                className="d-flex flex-column justify-content-center h5 mb-0"
              >
                <FaRegClock />
              </Col>
            </Row>
          </div>
        )}
        {!mobileVisible && (
          <>
            <p className="h5">{context.t("Upload File later")}</p>
            <p className="text-remark">
              {context.t("Proceed without file upload")}
            </p>
          </>
        )}

        {mobileVisible && (
          <p className="small text-secondary mt-2">
            {context.t("We currently support the following file formats:")}{" "}
            {allowedFormats.map((format, idx) => {
              return (
                <span key={idx}>
                  {format}
                  {allowedFormats.length - 1 === idx ? "" : ", "}
                </span>
              );
            })}
          </p>
        )}
        <Label>
          <Controller
            name="skip_file_upload"
            control={control}
            render={({ field }) => (
              <Input
                type={mobileVisible ? "hidden" : "checkbox"}
                checked={field.value}
                {...field}
              />
            )}
          />
          {!mobileVisible &&
            context.t(
              "I can provide Design Files and want to upload the files later."
            )}
        </Label>
      </div>
      <hr />
      <Row>
        <Col md={1} xs={2}>
          {/** TODO: Icon should be local */}
          <img
            src={`https://cdn.myfolie.de/media/products/filecheck.png`}
            className={"filecheck-field-icon w-100"}
            alt="filecheck icon"
            loading="lazy"
          />
        </Col>
        <Col md={8} xs={10} id="filecheck_1_id_details" className="mb-4">
          <p className="h5">
            <Controller
              name="filecheck_checkbox"
              control={control}
              render={({ field }) => (
                <Input type="checkbox" checked={field.value} {...field} />
              )}
            />
            {context.t("Filecheck.Filecheck.Title")}
          </p>
          <p>{context.t("Filecheck.Filecheck.Description")}</p>
          <span
            onClick={() =>
              setShowFilecheckMoreInformation(!showFilecheckMoreInformation)
            }
            className="btn-link hv-pointer"
          >
            {context.t("more information")}{" "}
            {!showFilecheckMoreInformation ? <FaCaretDown /> : <FaCaretUp />}
          </span>
        </Col>
        <Col xs={12} md={8}>
          <Collapse
            isOpen={showFilecheckMoreInformation}
            className="bg-light p-3"
          >
            <p className="h5">{context.t("Filecheck.Filecheck.Title")}</p>
            <p>
              <div
                dangerouslySetInnerHTML={{
                  __html: context.t("Filecheck.Filecheck.Popover"),
                }}
              />
            </p>
          </Collapse>
        </Col>
      </Row>

      <FileSizeModal
        proposeOptimizedSizes={
          !FILEEDITOR[editorTypeId].sections.includes("sizeselect")
        }
      />

      {/** START No Filecheck Warning Modal */}
      <Modal
        fade={false}
        isOpen={showFilecheckWarning}
        toggle={() => setShowFilecheckWarning(false)}
      >
        <ModalHeader toggle={() => setShowFilecheckWarning(false)}>
          {context.t("NoFilecheck.alert.title")}
        </ModalHeader>
        <ModalBody>
          <div
            dangerouslySetInnerHTML={{
              __html: context.t("NoFilecheck.alert.body"),
            }}
          />
          <p className="text-center mt-1">
            <a
              href={`/${language}/fileguide/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={fileguideIcon}
                alt={context.t("Production Data Guidelines")}
                height="20"
              />{" "}
              {context.t("Production Data Guidelines")}
            </a>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              setValue("filecheck_checkbox", true);
              setShowFilecheckWarning(false);
            }}
          >
            {context.t("Please do a file check")}
          </Button>{" "}
          <Button
            color="secondary"
            onClick={() => setShowFilecheckWarning(false)}
          >
            {context.t("I understand")}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

/**  define proptype for the 'translation' function  */
FileTab.contextTypes = {
  t: PropTypes.func,
};
export default FileTab;
